"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Container, Typography, MenuItem, Select,
    FormControl, InputLabel, useTheme, Card, Grid,
    Divider, Chip, Button, Avatar, LinearProgress,
    IconButton, Tab, Tabs,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SavingsIcon from "@mui/icons-material/Savings";
import {
    TrendingUpOutlined, TrendingDownOutlined,
    CalendarTodayOutlined, FileDownloadOutlined,
    ArrowUpward, ArrowDownward, SwapHorizOutlined,
    NorthEastOutlined, SouthWestOutlined,
} from "@mui/icons-material";

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    cream:   '#FAF9FC',
    green:   '#00C896',
    red:     '#FF4D6A',
};

type BreakdownItem = { category: string; amount: number; };
type Period = 'week' | 'month' | 'year';

const monthlyTrend = [
    { month: 'Oct', income: 120000, spending: 75000  },
    { month: 'Nov', income: 130000, spending: 90000  },
    { month: 'Dec', income: 115000, spending: 110000 },
    { month: 'Jan', income: 140000, spending: 85000  },
];

const topSpending = [
    { category: 'Food & Dining',     amount: 25000, pct: 29, color: '#FF6B35', emoji: '🍽️' },
    { category: 'Bills & Utilities', amount: 22000, pct: 26, color: '#FF9500', emoji: '⚡' },
    { category: 'Credit Payments',   amount: 20000, pct: 24, color: B.purple,  emoji: '💳' },
    { category: 'Subscriptions',     amount: 10000, pct: 12, color: '#007AFF', emoji: '📱' },
    { category: 'Others',            amount: 8000,  pct: 9,  color: '#636366', emoji: '📦' },
];

/* ── Custom donut tooltip ─────────────────────────────────────────── */
const DonutTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
        <Box sx={{
            background: B.dark,
            border: `1px solid ${alpha(B.light, .25)}`,
            borderRadius: 2, px: 1.5, py: 1,
            boxShadow: `0 8px 24px ${alpha(B.deep, .6)}`,
        }}>
            <Typography sx={{ color: alpha('#fff', .5), fontSize: '.62rem', letterSpacing: '.06em', textTransform: 'uppercase', mb: .25 }}>
                {d.name}
            </Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.88rem', fontVariantNumeric: 'tabular-nums' }}>
                ₦{(d.value as number).toLocaleString()}
            </Typography>
        </Box>
    );
};

/* ── Main ─────────────────────────────────────────────────────────── */
const ActivityScreen: React.FC = () => {
    const theme   = useTheme();
    const navigate = useNavigate();
    const isDark   = theme.palette.mode === 'dark';

    const [selectedBank, setSelectedBank] = useState("all");
    const [period,       setPeriod]       = useState<Period>('month');
    const [activeTab,    setActiveTab]    = useState(0);

    const [income]   = useState(140000);
    const [spending] = useState(85000);
    const netSavings = income - spending;
    const total      = income + spending;
    const incomePercentage   = total > 0 ? income / total : 0;
    const spendingPercentage = total > 0 ? spending / total : 0;

    // Fast count-up: 500ms
    const [animatedValue, setAnimatedValue] = useState(0);
    useEffect(() => {
        let start = 0;
        const duration  = 500;
        const stepTime  = 16;
        const increment = 1 / (duration / stepTime);
        const interval  = setInterval(() => {
            start += increment;
            if (start >= 1) { start = 1; clearInterval(interval); }
            setAnimatedValue(start);
        }, stepTime);
        return () => clearInterval(interval);
    }, []);

    const { score, grade } = calculateDisposableIncomeScore(netSavings, income);
    const savingsRate = ((netSavings / income) * 100).toFixed(0);

    const incomeBreakdown: BreakdownItem[] = [
        { category: "Salary",    amount: 80000 },
        { category: "Bonus",     amount: 10000 },
        { category: "Freelance", amount: 5000  },
    ];
    const spendingBreakdown: BreakdownItem[] = [
        { category: "Bills",              amount: 15000 },
        { category: "Food",               amount: 25000 },
        { category: "Credit Utilization", amount: 20000 },
        { category: "Subscriptions",      amount: 7000  },
    ];

    const pieData   = [
        { name: 'Income',   value: income   },
        { name: 'Spending', value: spending },
    ];
    const PIE_COLORS = [B.green, B.red];

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HEADER ══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 10, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -50, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.65rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .4 }}>
                            Financial Activity
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif" }}>
                            Overview
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {(['week', 'month', 'year'] as Period[]).map(p => (
                            <Box key={p} onClick={() => setPeriod(p)} sx={{
                                px: 1.25, py: .45, borderRadius: 10, cursor: 'pointer',
                                background: period === p ? alpha('#fff', .18) : alpha('#fff', .06),
                                border: `1px solid ${alpha('#fff', period === p ? .25 : .08)}`,
                                transition: 'all .2s',
                                '&:hover': { background: alpha('#fff', .14) },
                            }}>
                                <Typography sx={{ color: '#fff', fontSize: '.65rem', fontWeight: 700, letterSpacing: '.04em', opacity: period === p ? 1 : .55 }}>
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel sx={{ color: alpha('#fff', .5), '&.Mui-focused': { color: B.accent } }}>Bank</InputLabel>
                        <Select value={selectedBank} label="Bank" onChange={e => setSelectedBank(e.target.value)}
                                sx={{ color: '#fff', background: alpha('#fff', .08), backdropFilter: 'blur(8px)', '.MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .15) }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .3) }, '.MuiSvgIcon-root': { color: alpha('#fff', .5) } }}
                                MenuProps={{ PaperProps: { sx: { bgcolor: B.dark, color: '#fff' } } }}>
                            <MenuItem value="all">All Banks</MenuItem>
                            <MenuItem value="access">Access Bank</MenuItem>
                            <MenuItem value="zenith">Zenith Bank</MenuItem>
                            <MenuItem value="gtbank">GTBank</MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton size="small" sx={{ color: alpha('#fff', .5), background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`, '&:hover': { background: alpha('#fff', .15) } }}>
                        <FileDownloadOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                </Box>
            </Box>

            <Container maxWidth="md" sx={{ mt: -5, position: 'relative', zIndex: 1, px: { xs: 2, md: 3 } }}>

                {/* ── Net savings hero ── */}
                <Box sx={{
                    mb: 2, borderRadius: 3, overflow: 'hidden',
                    background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`,
                    boxShadow: `0 16px 48px ${alpha(B.deep, .45)}`,
                }}>
                    <Box sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.63rem', letterSpacing: '.16em', textTransform: 'uppercase', mb: .6 }}>
                                    Net Savings
                                </Typography>
                                <Typography sx={{ color: '#fff', fontWeight: 400, fontSize: '2.6rem', lineHeight: 1, fontFamily: "'Instrument Serif', serif", letterSpacing: '-.02em' }}>
                                    ₦{Math.round(netSavings * animatedValue).toLocaleString()}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={1.25}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 8, px: 1, py: .35 }}>
                                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 5px ${B.green}` }} />
                                        <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.05em' }}>
                                            {savingsRate}% SAVINGS RATE
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Mini donut — compact, dark tooltip */}
                            <Box sx={{ width: 72, height: 72 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={20} outerRadius={32} dataKey="value" strokeWidth={0} paddingAngle={2}>
                                            {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                        </Pie>
                                        <Tooltip content={<DonutTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2.5, borderColor: alpha('#fff', .08) }} />

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <NetStatRow label="Income" value={income} color={B.green} animatedValue={animatedValue} dir="in" />
                            </Grid>
                            <Grid item xs={6}>
                                <NetStatRow label="Spending" value={spending} color={B.red} animatedValue={animatedValue} dir="out" />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Pie legend strip */}
                    <Box sx={{ px: 3, py: 1.25, background: alpha('#000', .2), display: 'flex', gap: 3 }}>
                        {pieData.map((d, i) => (
                            <Box key={d.name} display="flex" alignItems="center" gap={.75}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: PIE_COLORS[i] }} />
                                <Typography sx={{ color: alpha('#fff', .4), fontSize: '.65rem', letterSpacing: '.06em', textTransform: 'uppercase' }}>{d.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Income & Spending cards ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <FinanceCard title="Income" amount={income} percentage={incomePercentage} color={B.green}
                                 icon={<TrendingUpOutlined />} onClick={() => navigate('/income-analysis')} animatedValue={animatedValue} change="+12%" changePositive />
                    <FinanceCard title="Spending" amount={spending} percentage={spendingPercentage} color={B.red}
                                 icon={<TrendingDownOutlined />} onClick={() => navigate('/spending-analysis')} animatedValue={animatedValue} change="−5%" changePositive />
                </Box>

                {/* ── Trend chart ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Monthly Trend</Typography>
                        <CalendarTodayOutlined sx={{ fontSize: 15, color: alpha(isDark ? '#fff' : B.ink, .3) }} />
                    </Box>
                    <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={monthlyTrend} barSize={16} barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke={alpha(isDark ? '#fff' : '#000', .05)} vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: alpha(isDark ? '#fff' : B.ink, .35) as string }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: alpha(isDark ? '#fff' : B.ink, .3) as string }} axisLine={false} tickLine={false} width={42} />
                            <Tooltip
                                formatter={(v: number) => [`₦${v.toLocaleString()}`, '']}
                                contentStyle={{ background: B.dark, border: `1px solid ${alpha(B.light, .2)}`, borderRadius: 10, fontSize: 12, color: '#fff' }}
                                itemStyle={{ color: B.accent, padding: 0 }}
                                labelStyle={{ color: alpha('#fff', .45), fontSize: 11, marginBottom: 2 }}
                                cursor={{ fill: alpha(isDark ? '#fff' : B.dark, .04) }}
                            />
                            <Bar dataKey="income"   fill={B.green} radius={[4, 4, 0, 0]} name="Income"   opacity={.85} />
                            <Bar dataKey="spending" fill={B.red}   radius={[4, 4, 0, 0]} name="Spending" opacity={.85} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Box display="flex" gap={3} mt={1}>
                        {[['Income', B.green], ['Spending', B.red]].map(([label, color]) => (
                            <Box key={label} display="flex" alignItems="center" gap={.75}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                                <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), letterSpacing: '.04em' }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Disposable income score ── */}
                <DisposableIncomeCard score={score} grade={grade} netSavings={netSavings} animatedValue={animatedValue} isDark={isDark} surfaceBg={surfaceBg} surfaceBdr={surfaceBdr} surfaceShd={surfaceShd} />

                {/* ── Top spending categories ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Top Spending</Typography>
                        <Button size="small" onClick={() => navigate('/spending-analysis')}
                                sx={{ color: B.purple, textTransform: 'none', fontWeight: 600, fontSize: '.7rem', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>
                    {topSpending.map((cat, i) => (
                        <Box key={i} sx={{ mb: i < topSpending.length - 1 ? 2.25 : 0 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={.8}>
                                <Box display="flex" alignItems="center" gap={1.25}>
                                    <Box sx={{ width: 30, height: 30, borderRadius: 2, background: alpha(cat.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem' }}>
                                        {cat.emoji}
                                    </Box>
                                    <Typography sx={{ fontSize: '.8rem', fontWeight: 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>{cat.category}</Typography>
                                </Box>
                                <Box textAlign="right">
                                    <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: isDark ? '#fff' : B.ink, fontVariantNumeric: 'tabular-nums' }}>₦{cat.amount.toLocaleString()}</Typography>
                                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .3) }}>{cat.pct}%</Typography>
                                </Box>
                            </Box>
                            <LinearProgress variant="determinate" value={cat.pct}
                                            sx={{ height: 4, borderRadius: 4, bgcolor: alpha(cat.color, .08), '& .MuiLinearProgress-bar': { background: cat.color, borderRadius: 4 } }} />
                        </Box>
                    ))}
                </Box>

                {/* ── Breakdown tabs ── */}
                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{
                        px: 2, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '.8rem', letterSpacing: '-.01em', color: alpha(isDark ? '#fff' : B.ink, .4), '&.Mui-selected': { color: B.purple } },
                        '& .MuiTabs-indicator': { background: B.purple, height: 2, borderRadius: 2 },
                    }}>
                        <Tab label="Income" />
                        <Tab label="Spending" />
                    </Tabs>
                    <Box sx={{ p: 2.5 }}>
                        {activeTab === 0 && <BreakdownSection items={incomeBreakdown}  color={B.green} total={income}  isDark={isDark} />}
                        {activeTab === 1 && <BreakdownSection items={spendingBreakdown} color={B.red}   total={spending} isDark={isDark} />}
                    </Box>
                </Box>

            </Container>
        </Box>
    );
};

/* ── Sub-components ─────────────────────────────────────────────────── */

const NetStatRow: React.FC<{ label: string; value: number; color: string; animatedValue: number; dir: 'in' | 'out' }> = ({ label, value, color, animatedValue, dir }) => (
    <Box display="flex" alignItems="center" gap={1.25}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, background: alpha(color, .15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {dir === 'in'
                ? <SouthWestOutlined sx={{ color, fontSize: 14 }} />
                : <NorthEastOutlined sx={{ color, fontSize: 14 }} />
            }
        </Box>
        <Box>
            <Typography sx={{ color: alpha('#fff', .38), fontSize: '.62rem', letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.88rem', fontVariantNumeric: 'tabular-nums' }}>
                ₦{Math.round(value * animatedValue).toLocaleString()}
            </Typography>
        </Box>
    </Box>
);

const FinanceCard: React.FC<{
    title: string; amount: number; percentage: number; color: string;
    icon: React.ReactNode; onClick: () => void; animatedValue: number;
    change: string; changePositive: boolean;
}> = ({ title, amount, percentage, color, icon, onClick, animatedValue, change, changePositive }) => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const surfaceBg = isDark ? alpha('#2D1B69', .55) : '#fff';
    return (
        <Box onClick={onClick} sx={{
            borderRadius: 3, p: 2.5, cursor: 'pointer',
            background: surfaceBg,
            border: `1px solid ${alpha(isDark ? '#fff' : '#0D0A1A', .07)}`,
            boxShadow: `0 4px 28px ${alpha('#1A0F3D', isDark ? .25 : .06)}`,
            transition: 'all .25s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(color, .18)}` },
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {React.cloneElement(icon as React.ReactElement, { sx: { color, fontSize: 17 } })}
                </Box>
                <Box sx={{ background: alpha(changePositive ? '#00C896' : '#FF4D6A', .1), borderRadius: 6, px: .9, py: .3 }}>
                    <Typography sx={{ fontSize: '.62rem', fontWeight: 700, color: changePositive ? '#00C896' : '#FF4D6A', letterSpacing: '.02em' }}>{change}</Typography>
                </Box>
            </Box>
            <Typography sx={{ color: alpha(isDark ? '#fff' : '#0D0A1A', .38), fontSize: '.65rem', letterSpacing: '.06em', textTransform: 'uppercase', mb: .4 }}>{title}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.02em', color: isDark ? '#fff' : '#0D0A1A', fontVariantNumeric: 'tabular-nums' }}>
                ₦{Math.round(amount * animatedValue).toLocaleString()}
            </Typography>
            <Box mt={1.5}>
                <Box sx={{ height: 3, bgcolor: alpha(color, .1), borderRadius: 4 }}>
                    <Box sx={{ width: `${(percentage * animatedValue * 100).toFixed(1)}%`, height: '100%', bgcolor: color, borderRadius: 4, transition: 'width .3s ease' }} />
                </Box>
                <Typography sx={{ fontSize: '.6rem', color, textAlign: 'right', mt: .4, fontWeight: 700, letterSpacing: '.02em' }}>
                    {(percentage * animatedValue * 100).toFixed(1)}%
                </Typography>
            </Box>
        </Box>
    );
};

const DisposableIncomeCard: React.FC<{
    score: number; grade: string; netSavings: number; animatedValue: number;
    isDark: boolean; surfaceBg: string; surfaceBdr: string; surfaceShd: string;
}> = ({ score, grade, netSavings, animatedValue, isDark, surfaceBg, surfaceBdr, surfaceShd }) => {
    const theme = useTheme();
    const scoreColor = score >= 75 ? B.green : score >= 55 ? '#FF9500' : B.red;
    return (
        <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.75}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(scoreColor, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SavingsIcon sx={{ color: scoreColor, fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '.85rem', color: isDark ? '#fff' : B.ink, letterSpacing: '-.02em' }}>Disposable Income</Typography>
                        <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .38) }}>Grade: <span style={{ color: scoreColor, fontWeight: 700 }}>{grade}</span></Typography>
                    </Box>
                </Box>
                <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400, fontSize: '2.2rem', color: scoreColor, lineHeight: 1 }}>
                    {Math.round(score * animatedValue)}
                </Typography>
            </Box>
            <LinearProgress variant="determinate" value={(score / 100) * 100 * animatedValue}
                            sx={{ height: 4, borderRadius: 4, mb: 2, bgcolor: alpha(scoreColor, .08), '& .MuiLinearProgress-bar': { background: scoreColor, borderRadius: 4 } }} />
            <Box sx={{ p: 1.75, bgcolor: alpha(isDark ? '#fff' : B.dark, .04), borderRadius: 2.5, borderLeft: `3px solid ${alpha(scoreColor, .4)}` }}>
                <Typography sx={{ fontSize: '.72rem', color: alpha(isDark ? '#fff' : B.ink, .45), mb: .2 }}>Available to spend</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '.9rem', color: isDark ? '#fff' : B.ink, fontVariantNumeric: 'tabular-nums' }}>
                    ₦{netSavings.toLocaleString()}
                </Typography>
            </Box>
        </Box>
    );
};

const BreakdownSection: React.FC<{
    items: BreakdownItem[]; color: string; total: number; isDark: boolean;
}> = ({ items, color, total, isDark }) => (
    <Box>
        {items.map((item, idx) => {
            const pct = total > 0 ? (item.amount / total) * 100 : 0;
            return (
                <Box key={idx} mb={idx < items.length - 1 ? 2.25 : 0}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={.8}>
                        <Box display="flex" alignItems="center" gap={1.25}>
                            <Box sx={{ width: 28, height: 28, borderRadius: 2, bgcolor: alpha(color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AttachMoneyIcon sx={{ color, fontSize: 14 }} />
                            </Box>
                            <Typography sx={{ fontSize: '.8rem', fontWeight: 500, letterSpacing: '-.01em', color: isDark ? '#fff' : B.ink }}>{item.category}</Typography>
                        </Box>
                        <Box textAlign="right">
                            <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: isDark ? '#fff' : B.ink, fontVariantNumeric: 'tabular-nums' }}>₦{item.amount.toLocaleString()}</Typography>
                            <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .3) }}>{pct.toFixed(1)}%</Typography>
                        </Box>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                                    sx={{ height: 4, borderRadius: 4, bgcolor: alpha(color, .08), '& .MuiLinearProgress-bar': { background: color, borderRadius: 4 } }} />
                </Box>
            );
        })}
    </Box>
);

function calculateDisposableIncomeScore(netSavings: number, income: number): { score: number; grade: string } {
    if (income <= 0) return { score: 0, grade: "Poor" };
    const ratio = netSavings / income;
    if (ratio >= 0.4) return { score: 90, grade: "Excellent" };
    if (ratio >= 0.2) return { score: 75, grade: "Good" };
    if (ratio >= 0)   return { score: 55, grade: "Fair" };
    return { score: 30, grade: "Poor" };
}

export default ActivityScreen;